import type {FunctionComponent} from 'react'
import {useEffect} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'

import type {Product, RootAndListContext, RootLoaderResponse} from '~/types'
import {useSendPartnerProductClickoutCustom, useReportCustomEvent} from '~/hooks/analytic-hooks'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
interface OptinmonsterMetadataProps {
  featureFlagVariation?: string
  directionToRedirect?: string
  dwsProduct?: Product
}

interface DWSData {
  productDiscount: number
  productTitle: string
  redirectLink: string
  productImage?: string
}

const SearchLinksDictionary = {
  US: 'https://www.amazon.com/s?k=replaceme',
  UK: 'https://www.amazon.co.uk/s?k=replaceme',
  CA: 'https://www.amazon.ca/s?k=replaceme',
  DE: 'https://www.amazon.de/s?k=replaceme',
  IT: 'https://www.amazon.it/s?k=replaceme',
  FR: 'https://www.amazon.fr/s?k=replaceme',
  ES: 'https://www.amazon.es/s?k=replaceme',
}

export const OptinmonsterMetadata: FunctionComponent<OptinmonsterMetadataProps> = ({
  featureFlagVariation,
  dwsProduct,
}) => {
  const {locale, featureFlags, serverSideVariables, trackAnalytics} = useRouteLoaderData('root') as RootLoaderResponse
  const {clickId, updateClickId} = useOutletContext<RootAndListContext>()

  const reportCustomEvent = useReportCustomEvent()
  const reportClickout = useSendPartnerProductClickoutCustom()

  const dwsData = validateDWSProduct(dwsProduct)

  function validateDWSProduct(product?: Product): DWSData | undefined {
    if (!product) return

    if (!((product?.discount ?? 0) > 1)) return

    return {
      productDiscount: product.discount ?? 1,
      productTitle: product.name.length > 100 ? product.name.substring(0, 100) + '...' : product.name,
      redirectLink: product.affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId),
      productImage: product.image,
    }
  }

  function addOptinmonsterScript(document: Document, user: string, account: string) {
    if (!document.querySelector(`[data-user="${user}"]`)) {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = 'https://a.omappapi.com/app/js/api.min.js'
      script.async = true
      script.dataset.user = user
      script.dataset.account = account
      document.getElementsByTagName('head')[0].appendChild(script)
    }
  }

  const submit = useSubmit()

  useEffect(() => {
    const Optin__redirectInfo = {
      clientClickId: Math.random().toString().slice(2, 11),
      trafficType: 'unknown',
      placementId: '000000000',
    }

    const handleOptinInit = (event: any) => {
      const optinHost = window.location.host
      const linksDictionary = SearchLinksDictionary

      //@ts-expect-error it's fine that if there is no link for this locale, we will use US link
      const directionLink: string = linksDictionary[locale] || linksDictionary.US

      const optinUrl = btoa(directionLink?.replace('replaceme', window.location.pathname.split('/')?.reverse()[0]))

      const optinRedirectLink = `https://${optinHost}/redirect?rf_partner_id=${event.detail.Campaign.data.id}&rf_placement_id=000000000&rf_click_source=optinmonster&source=amazon&url=${optinUrl}&clickId=${Optin__redirectInfo.clientClickId}&pageLoadId=000000000`
      if (featureFlagVariation) {
        event.detail.Dtr.setCustomVariable('redirect_link', dwsData?.redirectLink)
        event.detail.Dtr.setCustomVariable('productImage', `src=${dwsData?.productImage}`)
        event.detail.Dtr.setCustomVariable('productTitle', dwsData?.productTitle)
        event.detail.Dtr.setCustomVariable('productDiscount', dwsData?.productDiscount.toString())
      } else {
        event.detail.Dtr.setCustomVariable('redirect_link', optinRedirectLink)
      }
    }

    const handleAnalyticsTrack = (event: any) => {
      if ('conversion' === event.detail.Analytics.type) {
        reportCustomEvent('widget_seen', {
          widget_name: event.detail.Campaign.data.name,
          widget_place: event.detail.Campaign.data.type,
          partner_item_id: dwsProduct?.asin ?? '',
          discount: dwsProduct?.discount ?? 0,
        })

        reportClickout({
          item_list_id: window.location.pathname.split('/').reverse()[0],
          partner_item_id: event.detail.Campaign.data.id,
          item_name: event.detail.Campaign.data.name,
          affiliation: 'amazon',
          click_id: Number(Optin__redirectInfo.clientClickId),
          traffic: Optin__redirectInfo.trafficType,
          click_element_name: 'CTA Button',
          placement_id: Optin__redirectInfo.placementId,
          partner_item_brand: '',
          index: 0,
          target: '',
          gclid: undefined,
          sys_id: undefined,
          is_dws: false,
        })
      } else {
        reportCustomEvent('widget_clicked', {
          widget_name: event.detail.Campaign.data.name,
          count_products: 0,
          count_dws_items: 0,
          widget_place: event.detail.Campaign.data.type,
          partner_item_id: dwsProduct?.asin ?? '',
          discount: dwsProduct?.discount ?? 0,
        })
      }
    }

    //@ts-expect-error set window property for optinmonster
    window.Optin__featureFlagVariation = featureFlagVariation && dwsData ? featureFlagVariation : 'none'

    const handleLoad = () => addOptinmonsterScript(document, '269906', '287530')

    document.addEventListener('om.Dtr.init', handleOptinInit)
    document.addEventListener('om.Analytics.track', handleAnalyticsTrack)
    window.addEventListener('load', handleLoad)

    return () => {
      document.removeEventListener('om.Dtr.init', handleOptinInit)
      document.removeEventListener('om.Analytics.track', handleAnalyticsTrack)
      window.removeEventListener('load', handleLoad)
    }
  }, [
    locale,
    reportClickout,
    reportCustomEvent,
    dwsData,
    featureFlagVariation,
    dwsProduct,
    featureFlags,
    submit,
    clickId,
    serverSideVariables,
    trackAnalytics,
    updateClickId,
  ])

  return <></>
}
